import { Injectable } from '@angular/core';
import { environment } from '@env';
import { Observable } from 'rxjs';

import { AssetService } from '../../models/assetservice.class';
import { RecordReports } from '../../models/record/recordreports.class';
import { Report } from '../../models/report.interface';
import { ReportMetaData } from '../../models/reportmetadata.interface';
import { AssetsService } from '../assets/assets.service';
import { CommonUtilsService } from '../commonutils/common-utils.service';
import { InstancesService } from '../instances/instances.service';
import { LocationsService } from '../locations/locations.service';
import { RootScopeService } from '../rootscope/rootscope.service';
import { UtilsService } from '../utils/utils.service';

@Injectable({
    providedIn: 'root',
})
export class CommonReportsService {
    analyticsMetaData: {
        [property: string]: RecordReports[];
    };

    constructor(
        private _instances: InstancesService,
        private _locations: LocationsService,
        private _utils: CommonUtilsService,
        private _assets: AssetsService,
        private _libUtils: UtilsService,
        protected R: RootScopeService
    ) {}

    generateReports = (
        orgNamesMap,
        reports: UIReport[],
        instanceId: string,
        assetId: string,
        currentApp: AssetService,
        noAlerts: boolean
    ) => {
        return new Promise(resolve => {
            const promises = [];
            promises[0] = this.getInstanceReports(instanceId, assetId, currentApp, noAlerts);
            promises[1] = this.getInstanceAnalytics(instanceId, assetId, currentApp, noAlerts);
            Promise.all(promises).then(async ([{ reportsResponse }, { analyticsResponse, analyticsMetaData }]) => {
                const analytics: Report[] = analyticsResponse;
                const providerIds = this._libUtils.getProviderIds(undefined, []);
                reportsResponse.forEach(report => {
                    !providerIds?.includes(report?.transformationRepositoryId) && providerIds.push(report.transformationRepositoryId);
                });
                analytics.forEach(record => {
                    !providerIds?.includes(record?.transformationRepositoryId) && providerIds.push(record.transformationRepositoryId);
                });
                Object.keys(analyticsMetaData).forEach(key => {
                    !providerIds?.includes(key) && providerIds.push(key);
                });
                orgNamesMap = await this.getOrgNamesByIds(providerIds, noAlerts);
                if (reports === undefined) {
                    reports = [];
                } else {
                    reports.splice(0);
                }
                providerIds.forEach(id => {
                    (analyticsMetaData[id] || []).forEach(report => {
                        this.generateReportsOrganizationObject(orgNamesMap, reports, id, report);
                    });
                });
                [...analytics, ...reportsResponse].forEach(report => {
                    this.generateReportsOrganizationObject(orgNamesMap, reports, report.transformationRepositoryId, undefined, report);
                });
                resolve(reports);
            });
        });
    };

    protected getInstanceReports = (instanceId, assetId, currentApp, noAlerts: boolean) => {
        return new Promise((resolve: (data: { reportsResponse: Report[] }) => void) => {
            const restApiName = this._libUtils.getRestApiName(currentApp);
            let reportsData;
            if (restApiName) {
                reportsData = {
                    restApiName,
                    assetDataId: instanceId,
                    noAlerts: noAlerts,
                };
            } else {
                reportsData = {
                    assetDataId: instanceId,
                    assetMetaUId: assetId || currentApp.assetMetaUId,
                    noAlerts: noAlerts,
                };
            }
            this._instances.fetchReports(reportsData).then(async ({ reports }) => {
                // As we have cache control i'm using below method again instead of storing in localstorage using it
                const displaynames = await this.getChainDisplayNames(currentApp.restApiName, assetId);
                reports?.forEach(report => {
                    report.hideRegenerate = true;
                    report.chainName = displaynames[report.chainName];
                });
                resolve({
                    reportsResponse: reports,
                });
            });
        });
    };

    protected getInstanceAnalytics = (instanceId: string, assetId: string, currentApp: AssetService, noAlerts: boolean) => {
        return new Promise(resolve => {
            this.getAnalyticNames(
                {
                    instanceId,
                    assetId,
                    currentApp,
                    noAlerts,
                },
                res => {
                    this.getAnalyticsByProviderId(instanceId, currentApp, noAlerts).then(async responseObject => {
                        // As we have cache control i'm using below method again instead of storing in localstorage using it
                        const displaynames = await this.getChainDisplayNames(currentApp.restApiName, assetId);
                        const reports = [];
                        Object.keys(responseObject || {}).forEach(analyticKey => {
                            (responseObject[analyticKey] || []).forEach((report, index) => {
                                responseObject[analyticKey][index].chainName = displaynames[report.chainName];
                                reports.push(report);
                            });
                        });
                        resolve({ analyticsResponse: reports, analyticsMetaData: res || {} });
                    });
                }
            );
        });
    };

    getChainDisplayNames = (restApiName: string, assetId: string) => {
        return new Promise(resolve => {
            this._assets.getChainDisplayNames({ restApiName, assetId }).then(res => {
                resolve(res.chainNameVsChainDisplayName);
            });
        });
    };

    generateReportsOrganizationObject = (orgNamesMap, reports, organizationId: string, reportMetaData: ReportMetaData, report?: Report) => {
        let organization = reports.find(organizationObject => organizationObject.id === organizationId);
        if (!organization) {
            const providerOrgId = this.getProviderOrganizationId();
            organization = {
                id: organizationId,
                name: providerOrgId && organizationId === providerOrgId ? 'Default' : orgNamesMap[organizationId],
                chains: [],
            };
            reports.push(organization);
        }
        this.generateReportsChainObject(organization, reportMetaData, report);
    };

    private getProviderOrganizationId = (): string | undefined => {
        const currentOrganization = this._utils.getFromStorage('currentOrganization');
        return currentOrganization?.providerOrganizationId;
    };

    getOrgNamesByIds = (orgIdList, noAlerts: boolean) => {
        return new Promise((resolve: (orgIdVsNamesMap: { [property: string]: string }) => void) => {
            const cachedOrgidsObj = this.getCachedOrgNames(orgIdList);
            if (!cachedOrgidsObj || (cachedOrgidsObj && cachedOrgidsObj.orgIdList && cachedOrgidsObj.orgIdList.length > 0)) {
                const data = {
                    orgIds: (cachedOrgidsObj && cachedOrgidsObj.orgIdList) || orgIdList,
                    noAlerts,
                };
                this._locations.getOrgNamesByIds(data).then(res => {
                    const storedOrgIdsList = this._utils.getFromStorage('orgIdVsNames');
                    const orgNamesList = {
                        ...(cachedOrgidsObj?.orgIdVsNamesList || {}),
                        ...(res || {}),
                        ...(storedOrgIdsList || {}),
                    };
                    this._utils.setInStorage('orgIdVsNames', orgNamesList);
                    resolve(orgNamesList);
                });
            } else {
                resolve(cachedOrgidsObj?.orgIdVsNamesList);
            }
        });
    };

    getCachedOrgNames = orgIdsList => {
        const orgIdVsNames = this._utils.getFromStorage('orgIdVsNames');
        const obj = {};
        const cloneOrgidsList = CommonUtilsService.cloneObject(orgIdsList);
        if (orgIdVsNames) {
            const totalKeys = Object.keys(orgIdVsNames);
            for (let i = 0; i < orgIdsList.length; i++) {
                if (totalKeys.indexOf(orgIdsList[i]) > -1) {
                    obj[orgIdsList[i]] = orgIdVsNames[orgIdsList[i]];
                    cloneOrgidsList.forEach((id, index) => {
                        if (id === orgIdsList[i]) {
                            cloneOrgidsList.splice(index, 1);
                        }
                    });
                }
            }
            const data = {
                orgIdList: cloneOrgidsList,
                orgIdVsNamesList: obj,
            };
            return data;
        } else {
            return undefined;
        }
    };

    protected generateReportsChainObject = (
        organization: {
            name: string;
            displayName: string;
            id: string;
            chains: {
                name: string;
                displayName: string;
                defaultReport: boolean;
                transformations: { name: string; reports: UIReport[] }[];
                isOnStageEvent: boolean;
            }[];
        },
        reportMetaData: ReportMetaData,
        report?: Report
    ) => {
        const chains = organization.chains;
        const name = (reportMetaData && reportMetaData.name) || (report && report.chainName);
        const displayName = (reportMetaData && reportMetaData.displayName) || (report && report.chainName);
        const defaultReport = reportMetaData && reportMetaData.defaultReport;
        const restAPIName = reportMetaData?.restAPIName;
        const isOnStageEvent = report?.isOnStageEvent;
        let chain: {
            name: string;
            displayName: string;
            defaultReport: boolean;
            transformations: any[];
            restAPIName: string;
            isOnStageEvent: boolean;
        } = this.getChain(chains, name);
        if (!chain) {
            chain = {
                name,
                displayName,
                defaultReport,
                transformations: [],
                restAPIName,
                isOnStageEvent,
            };
            chains.push(chain);
        }
        const transformations = chain.transformations;
        if (reportMetaData?.orderedTransformations?.length > 0) {
            reportMetaData.orderedTransformations
                .filter(order => order.targetStores?.indexOf('TAXILLA') > -1)
                .forEach(order => {
                    this.generateReportsTransformationObject(chain, transformations, order);
                });
        } else if (report?.transformationName) {
            this.generateReportsTransformationObject(
                chain,
                transformations,
                { name: report.transformationName, reportNameConfig: undefined, targetFieldIds: undefined, targetStores: undefined },
                report
            );
        }
    };

    protected generateReportsTransformationObject = (
        chain: { name: string; transformations: { name: string; reports: any[] }[] },
        transformations: { name: string; reports: any[] }[],
        reportMetaData: ReportMetaData['orderedTransformations'][0],
        report?: Report
    ) => {
        const name = reportMetaData?.name || report?.transformationName;
        let transformation = this.getTransformation(transformations, reportMetaData.name);
        if (!transformation) {
            transformation = {
                name,
                reports: [],
            };
            transformations.push(transformation);
        }
        this.generateReportsMetdaDataObject(transformation.reports, chain.name, transformation.name, report);
    };

    protected getTransformation = (
        transformations,
        transformationName
    ): {
        name: string;
        reports: {
            chainName: string;
            reportCategory: string;
            transformationName: string;
            reportsList: Report[];
        }[];
    } => {
        return transformations.find(transformation => transformation.name === transformationName);
    };

    protected generateReportsMetdaDataObject = (reportMetaDataList, chainName, transformationName, report?: Report) => {
        let reportMetaData = this.getReportsMetaData(reportMetaDataList, chainName, transformationName);
        if (!reportMetaData) {
            reportMetaData = {
                chainName,
                reportCategory: 'ANALYTICS',
                transformationName,
                name: transformationName,
                reportsList: [],
            };
            reportMetaDataList.push(reportMetaData);
        }
        if (report) {
            report['reportLink'] =
                environment.taxilla_api +
                '/process/v1/files?fileUrl=' +
                encodeURIComponent(report.reportUrl) +
                '&fileName=' +
                encodeURIComponent(report.reportName);
            report['isPrinatable'] = this._libUtils.isInPdf(report.reportName);
            report.checkSum && (report.checkSum = this._libUtils.convertCheckSumStrToKeyValue(report.checkSum));
            reportMetaData.reportsList.push(report);
        }
    };

    protected getReportsMetaData = (
        reportMetaDataList,
        chainName: string,
        transformationName: string
    ): {
        chainName: string;
        reportCategory: string;
        transformationName: string;
        reportsList: Report[];
        name: string;
        checkSum?: string;
    } => {
        return reportMetaDataList.find(item => item.chainName === chainName && item.transformationName === transformationName);
    };

    protected getAnalyticNames = (data: { instanceId: string; assetId: string; currentApp: AssetService; noAlerts: boolean }, callback) => {
        this._instances.getAnalyticReportsMetaData(
            {
                instanceId: data.instanceId,
                providerOrgId: undefined,
                service: data.currentApp,
                assetId: data.assetId,
                noAlerts: data.noAlerts,
            },
            {
                successCallback: res => {
                    this.analyticsMetaData = res;
                    callback(res);
                },
            }
        );
    };

    protected getChain = (chains, chainName) => {
        return chains.find(chain => chain.name === chainName || chain.displayName === chainName);
    };

    protected getAnalyticsByProviderId = (instanceId: string, currentApp: AssetService, noAlerts: boolean) => {
        return new Promise(resolve => {
            this._instances.getAnalyticReportsWithNames(
                {
                    instanceId,
                    service: currentApp,
                    noAlerts: noAlerts,
                },
                undefined,
                {
                    successCallback: (res: any) => {
                        resolve(res.response || res);
                    },
                }
            );
        });
    };

    generateReport = ({
        chainName,
        instanceId,
        orgNamesMap,
        reports,
        assetId,
        currentService,
        dontFetchReports,
        noAlerts,
    }: {
        chainName: string;
        instanceId: string;
        orgNamesMap: { [property: string]: string };
        reports: UIReport[];
        assetId: string;
        currentService: AssetService;
        dontFetchReports?: boolean;
        noAlerts?: boolean;
    }) => {
        return new Promise((resolve, reject) => {
            const user = this._utils.getFromStorage('user');
            let repositoryId = '';
            reports.forEach(element => {
                element.chains.forEach(chain => {
                    if (chainName === chain.name) {
                        repositoryId = element.id;
                    }
                });
            });
            noAlerts && this._libUtils.showLoading();
            this._instances.generateReport(
                {
                    chainName,
                    instanceId,
                    service: currentService,
                    userName: user.id,
                    repositoryId,
                    noAlerts: noAlerts,
                },
                {
                    successCallback: async () => {
                        noAlerts && this._libUtils.hideLoading();
                        noAlerts && this._libUtils.alertSuccess('Report generation is in progress, please check the notifications');
                        if (!dontFetchReports) {
                            await this.generateReports(orgNamesMap, reports, instanceId, assetId, currentService, noAlerts);
                        }
                        resolve(reports);
                    },
                    failureCallback: async () => {
                        reject();
                    },
                }
            );
        });
    };

    generateAuditReports = async (
        orgNamesMap,
        reports: UIReport[],
        instanceId: string,
        assetId: string,
        currentApp: AssetService,
        noAlerts: boolean,
        callBack?: () => void
    ) => {
        const promises = [];
        promises[0] = this.getGeneratedAuditReports(instanceId, assetId, currentApp, noAlerts);
        promises[1] = this.getInstanceAuditAnalytics(assetId, currentApp, noAlerts);
        Promise.all(promises).then(([{ orgIdVsNamesMap, reportsResponse }, { analyticsMetaData }]) => {
            orgNamesMap = orgIdVsNamesMap;
            const providerIds = this._libUtils.getProviderIds(undefined, []);
            providerIds.push(...Object.keys(analyticsMetaData));
            if (reports === undefined) {
                reports = [];
            } else {
                reports.splice(0);
            }
            providerIds.forEach(id => {
                (analyticsMetaData[id] || []).forEach(report => {
                    this.generateReportsOrganizationObject(orgNamesMap, reports, id, report);
                });
            });
            (reportsResponse || []).forEach(report => {
                this.generateReportsOrganizationObject(orgNamesMap, reports, report.transformationRepositoryId, undefined, report);
            });

            callBack?.();
        });
    };

    getGeneratedAuditReports = (instanceId, assetId, currentApp, noAlerts: boolean) => {
        return new Promise((resolve: (data: { reportsResponse: Report[]; orgIdVsNamesMap: { [property: string]: string } }) => void) => {
            const data = {
                assetId: assetId || currentApp.assetMetaUId,
                instanceId: instanceId,
                noAlerts: noAlerts,
            };

            this._instances.getGeneratedAuditReports(data, {
                successCallback: async (response, orgIdVsNamesMap) => {
                    const providerIds = this._libUtils.getProviderIds(undefined, []);
                    const orgIdVsNames = await this.getOrgNamesByIds(providerIds, noAlerts);
                    resolve({
                        reportsResponse: response,
                        orgIdVsNamesMap: orgIdVsNamesMap || orgIdVsNames,
                    });
                },
            });
        });
    };

    getInstanceAuditAnalytics = (assetId, currentApp, noAlerts?: boolean) => {
        return new Promise(resolve => {
            this._instances.getAuditReportMetadata(
                {
                    assetId: assetId || currentApp.assetMetaUId,
                    noAlerts,
                },
                {
                    successCallback: async res => {
                        resolve({ analyticsMetaData: res || {} });
                    },
                    failureCallback: () => {},
                }
            );
        });
    };

    generateAuditReport = ({
        chainName,
        instanceId,
        orgNamesMap,
        reports,
        assetId,
        currentApp,
        noAlerts,
        callback,
        skipFetchAuditReports,
    }: {
        chainName: string;
        instanceId: string;
        orgNamesMap: { [property: string]: string };
        reports: UIReport[];
        assetId: string;
        currentApp: AssetService;
        noAlerts: boolean;
        callback?: () => void;
        skipFetchAuditReports?: boolean;
    }) => {
        this._instances.generateAuditReport(
            {
                assetId: assetId || currentApp.assetMetaUId,
                instanceId: instanceId,
                chainName: chainName,
                noAlerts,
            },
            {
                successCallback: res => {
                    this._libUtils.alertSuccess(res?.msg || 'Report generation is in progress, please check the notifications');
                    !skipFetchAuditReports && this.generateAuditReports(orgNamesMap, reports, instanceId, assetId, currentApp, noAlerts);
                    callback?.();
                },
            }
        );
    };

    /**
     * Method to get Report Processes
     * @param data contains service ID
     * @param s Success callback
     * @param f Failure callback
     */
    getReportProcesses = (data: {
        serviceId: string;
        size?: number;
        previousPagingState?: string;
        noAlerts?: boolean;
        timeRange?: any[];
    }) => {
        return new Observable(observer => {
            this._instances.getReportProcesses(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to generate Search Report
     */
    generateSearchReport = (data: { assetId: string; requestId: string; repositoryId: string; transformation: string }) => {
        return new Observable(observer => {
            this._instances.generateSearchReport(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res);
                    observer.complete();
                }
            );
        });
    };
}

export interface UIReport {
    name: string;

    id: string;

    chains: {
        name: string;
        displayName?: string;
        transformations: {
            name: string;
            reports: UIReportData[];
        }[];
        restAPIName?: string;
    }[];
}

export interface UIReportData {
    chainName: string;

    reportCategory: string;

    transformationName: string;

    reportsList: Report[];

    name: string;
}
